import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Icon,
  IconButton,
  TextInput,
} from '@socialchorus/shared-ui-components';
import parse, {
  domToReact,
  Element,
  HTMLReactParserOptions,
} from 'html-react-parser';
import { usePatronSelector } from '../../common/use-patron-selector';
import programSelectors from '../../models/program/selectors';
import { useChatbotSearchQuery } from '../../common/queries/use-chatbot-query';
import { ChatbotSearchResult } from '../../models/chatbot/types';
import { formatNarrowTimeAgo } from '../../lib/format-narrow-time-ago';
import { trackChatbotHistorySearch } from '../../models/assistant/analytics/analytics';
import styles from './ChatHistorySearch.module.scss';

export interface ChatHistorySearchProps {
  onSelectThread: (threadId: string, query: string) => void;
  onSearchActiveChange?: (active: boolean) => void;
  initialQuery?: string;
}

const DEBOUNCE_MS = 300;

const highlightParseOptions: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (
      'name' in domNode &&
      (domNode.name === 'script' || domNode.name === 'style')
    ) {
      return <></>;
    }

    if (!(domNode instanceof Element) || domNode.type !== 'tag') {
      return undefined;
    }

    if (domNode.name === 'em') {
      return <em>{domToReact(domNode.children, highlightParseOptions)}</em>;
    }

    return <>{domToReact(domNode.children, highlightParseOptions)}</>;
  },
};

const ChatHistorySearch: React.FC<ChatHistorySearchProps> = ({
  onSelectThread,
  onSearchActiveChange,
  initialQuery,
}) => {
  const { t } = useTranslation();
  const programId = usePatronSelector(programSelectors.getProgramId);
  const programIdStr = programId ? String(programId) : '';

  const [inputValue, setInputValue] = useState(initialQuery ?? '');
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery ?? '');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setDebouncedQuery(inputValue.trim());
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [inputValue]);

  useEffect(() => {
    onSearchActiveChange?.(debouncedQuery.length > 0);
  }, [debouncedQuery, onSearchActiveChange]);

  const { data, isLoading, isFetching } = useChatbotSearchQuery(
    programIdStr,
    debouncedQuery
  );

  const results = data?.data ?? [];
  const hasActiveQuery = debouncedQuery.length > 0;
  const showLoading = hasActiveQuery && (isLoading || isFetching);
  const showNoResults = hasActiveQuery && !isLoading && results.length === 0;
  const showResults = hasActiveQuery && results.length > 0;

  // Emit one analytics event per user-initiated committed search. A query
  // restored via back-navigation (seeded through initialQuery) is not a new
  // user search and should not double-count, so we pre-seed the ref on mount.
  const lastTrackedQuery = useRef<string | null>(initialQuery ?? null);
  useEffect(() => {
    if (!hasActiveQuery) return;
    if (isLoading || isFetching) return;
    if (lastTrackedQuery.current === debouncedQuery) return;
    lastTrackedQuery.current = debouncedQuery;
    trackChatbotHistorySearch({
      query_length: debouncedQuery.length,
      result_count: results.length,
    });
  }, [debouncedQuery, hasActiveQuery, isLoading, isFetching, results.length]);

  const handleClear = useCallback(() => {
    setInputValue('');
    setDebouncedQuery('');
  }, []);

  const handleResultKeyDown = useCallback(
    (e: React.KeyboardEvent, threadId: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectThread(threadId, debouncedQuery);
      }
    },
    [onSelectThread, debouncedQuery]
  );

  return (
    <div className={styles.searchContainer} data-testid="chat-history-search">
      <div className={styles.searchInputWrapper}>
        <TextInput
          fullWidth
          leftWidget={<Icon size={16}>search</Icon>}
          rightWidget={
            inputValue ? (
              <IconButton
                size="extra-compact"
                iconName="close"
                onClick={handleClear}
                aria-label={t('common.clear', 'Clear')}
                data-testid="search-clear"
              />
            ) : undefined
          }
          size="compact"
          placeholder={t('assistant.searchHistory', 'Search conversations...')}
          onChange={(value) => setInputValue(value)}
          value={inputValue}
          data-testid="search-input"
        />
      </div>

      {showLoading && (
        <div className={styles.loadingState} data-testid="search-loading">
          <span className={styles.loadingText}>
            {t('common.searching', 'Searching...')}
          </span>
        </div>
      )}

      {showNoResults && (
        <div className={styles.noResults} data-testid="search-no-results">
          <span className={styles.noResultsText}>
            {t('assistant.noSearchResults', 'No results found')}
          </span>
        </div>
      )}

      {showResults && (
        <div
          className={styles.searchResults}
          data-testid="search-results"
          role="list"
        >
          {results.map((result: ChatbotSearchResult) => (
            <div
              key={result.threadId}
              className={styles.searchResultItem}
              role="listitem"
              tabIndex={0}
              onClick={() => onSelectThread(result.threadId, debouncedQuery)}
              onKeyDown={(e) => handleResultKeyDown(e, result.threadId)}
              data-testid={`search-result-${result.threadId}`}
            >
              <div className={styles.resultTitle}>{result.threadTitle}</div>
              <div
                className={styles.resultSnippet}
                data-testid={`search-result-snippet-${result.threadId}`}
              >
                {parse(result.matchedContent, highlightParseOptions)}
              </div>
              {result.lastMessageAt && (
                <div className={styles.resultTimestamp}>
                  {formatNarrowTimeAgo(result.lastMessageAt)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatHistorySearch;
